'use client'

import { useQuery } from '@tanstack/react-query'

const KATANA_BALANCES_URL = 'https://api.katana.network/v1/tokens/balances'

interface RawPortfolioToken {
  token_address: string
  name: string
  symbol: string
  decimals: number
  balance: number
  price: number | null
  value_usd: number | null
  logo_url: string | null
}

export interface PortfolioToken {
  token_address: string
  name: string
  symbol: string
  decimals: number
  balance: number
  price: number | null
  value_usd: number | null
  logo_url: string | null
}

interface KatanaBalancesResponse {
  balances: RawPortfolioToken[]
}

export interface PortfolioBalances {
  totalUSD: number
  tokens: PortfolioToken[]
}

function formatBalance(rawBalance: number, decimals: number): number {
  return rawBalance / 10 ** decimals
}

async function fetchPortfolioBalances(
  address: string,
): Promise<PortfolioBalances> {
  const response = await fetch(`${KATANA_BALANCES_URL}/${address}`)
  if (!response.ok) {
    throw new Error(`Failed to fetch balances: ${response.status}`)
  }

  const data: KatanaBalancesResponse = await response.json()
  const tokens = data.balances
    .filter((t) => t.balance > 0)
    .map((t) => ({
      ...t,
      balance: formatBalance(t.balance, t.decimals),
    }))
  const totalUSD = tokens.reduce((sum, t) => sum + (t.value_usd ?? 0), 0)

  return { totalUSD, tokens }
}

export function usePortfolioBalances(address: string | undefined) {
  return useQuery({
    queryKey: ['portfolio-balances', address],
    queryFn: () => fetchPortfolioBalances(address!),
    enabled: !!address,
    refetchInterval: 600_000,
  })
}
